import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { GeneralResMock } from '../types/general.type'
import { MARIncludedMock, MARResMock } from '../types/mar.type'
import { MedicationDataMock, MedicationIncluded } from '../types/medication.type'
import { WardStockOption } from '../types/wardstock.type'

type Props = {
  page?: number | string
  limit?: number | string
  sort_dir?: 'asc' | 'desc'
  id?: string
  status?: string
  encounter_id?: string
  date_from?: string
  date_to?: string
  prn?: boolean
  patient_id?: string
  period_of_day?: string
  sort_by?: string
  schedule_for?: string
  medication_id?: string
  encounter_type?: string
}

export const useMedAdministration = ({
  date_from,
  date_to,
  encounter_id,
  id,
  limit,
  page,
  patient_id,
  status,
  period_of_day,
  prn,
  sort_by = 'scheduled_for',
  sort_dir = 'desc',
  schedule_for,
  medication_id,
  encounter_type
}: Props = {}) => {
  const { data, isLoading: medIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MARResMock[], MARIncludedMock[]>>(
        'medication_administrations',
        {
          params: {
            ...(page && { 'page[number]': page }),
            ...(limit && { 'page[size]': limit }),
            ...(sort_by && { sort_by }),
            ...(sort_dir && { sort_dir }),
            ...(status && status !== 'all' && { status }),
            ...(encounter_id && { encounter_id }),
            ...(period_of_day && { period_of_day: period_of_day === 'All' ? '' : period_of_day }),
            ...(date_from && { date_from }),
            ...(date_to && { date_to }),
            ...(medication_id && { medication_id })
          }
        }
      )
      return data
    },
    queryKey: [
      'medication_administration',
      { date_from, date_to, encounter_id, id, limit, page, period_of_day, sort_dir, sort_by, status }
    ]
  })

  const { data: groupAdministration, isLoading: groupIsLoading } = useQuery({
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MARResMock[], MARIncludedMock[]>>(
        'medication_administrations/grouped_by_patient',
        {
          params: {
            ...(page && { 'page[number]': page }),
            ...(limit && { 'page[size]': limit }),
            ...(sort_by && { sort_by }),
            ...(sort_dir && { sort_dir }),
            ...(status && status !== 'all' && { status }),
            ...(encounter_id && { encounter_id }),
            ...(id && { medication_id: id }),
            ...(schedule_for && { schedule_for }),
            ...(encounter_type && { encounter_type })
          }
        }
      )
      return data
    },
    queryKey: [
      'medication_administration',
      'groupx_administration',
      { encounter_id, id, limit, page, sort_dir, sort_by, status, schedule_for, encounter_type }
    ]
  })

  const { data: prnMedications } = useQuery({
    enabled: !!prn,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<MedicationDataMock[], MedicationIncluded[]>>(
        `medications?prn=${prn}&status[]=verified`,
        {
          params: {
            ...(patient_id && { patient_id })
          }
        }
      )
      return data
    },
    queryKey: ['medication_prn', { prn }]
  })

  const { data: medication } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<any>>(`medication_administrations/${id}`)
      return data
    },
    queryKey: ['medication_administration', { id }]
  })

  const { data: wardStockOptions, isLoading: wardStockOptionsIsLoading } = useQuery({
    enabled: !!id,
    queryFn: async () => {
      const { data } = await AXIOS_INSTANCE.get<GeneralResMock<WardStockOption[]>>(
        'medication_administrations/ward_stock_options',
        {
          params: {
            ...(medication_id && { medication_id })
          }
        }
      )
      return data
    },
    queryKey: ['medication_administration', 'ward_stock_options', { medication_id }]
  })

  const { mutateAsync: createMedicationAdministration } = useMutation({
    mutationFn: async (formVal: any) => {
      if (!encounter_id) {
        throw new Error('Encounter ID is required to create medication administration')
      }
      const { data } = await AXIOS_INSTANCE.post(`encounters/${encounter_id}/medication_administrations`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medication_administration'] })
      queryClient.invalidateQueries({ queryKey: ['administration'] })
    }
  })

  const { mutateAsync: updateMedicationAdministration } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      await AXIOS_INSTANCE.patch(`medication_administrations/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['medication_administration'] })
      queryClient.invalidateQueries({ queryKey: ['administration'] })
    }
  })

  return {
    createMedicationAdministration,
    isLoading: medIsLoading,
    groupAdministration: groupAdministration?.data,
    groupIncluded: groupAdministration?.included,
    groupPagination: groupAdministration?.meta.pagination,
    groupIsLoading,
    medication,
    medications: data?.data,
    medicationsIncluded: data?.included,
    pagination: data?.meta.pagination,
    prnMedications,
    wardStockOptions: wardStockOptions?.data,
    wardStockOptionsIsLoading,
    updateMedicationAdministration,
    marStats: data?.meta.mar_stats
  }
}
